import { EikoLogo } from '@/components/EikoLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { useInvitation } from '@/hooks/useInvitation';
import { roleMapping } from '@/lib/roleMapping';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { Building2, CheckCircle, Loader2, Mail, Shield, XCircle } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useParams } from 'react-router-dom';
import { z } from 'zod';

const log = createLogger('InvitationView');

const registerSchema = z
  .object({
    firstName: z.string().min(2, 'Le prénom doit contenir au moins 2 caractères'),
    lastName: z.string().min(2, 'Le nom doit contenir au moins 2 caractères'),
    password: z
      .string()
      .min(8, 'Le mot de passe doit contenir au moins 8 caractères')
      .regex(/[A-Z]/, 'Le mot de passe doit contenir au moins une majuscule')
      .regex(/[a-z]/, 'Le mot de passe doit contenir au moins une minuscule')
      .regex(/[0-9]/, 'Le mot de passe doit contenir au moins un chiffre'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Les mots de passe ne correspondent pas',
    path: ['confirmPassword'],
  });

type RegisterFormData = z.infer<typeof registerSchema>;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20, filter: 'blur(4px)' },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.95, filter: 'blur(8px)' },
  visible: {
    opacity: 1,
    scale: 1,
    filter: 'blur(0px)',
    transition: {
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  },
};

const inputClassName = cn(
  'h-11 rounded-lg border-stone-200 bg-white',
  'text-stone-900 placeholder:text-stone-400',
  'transition-all duration-300',
  'focus-visible:border-emerald-500/50',
  'focus-visible:ring-[3px] focus-visible:ring-emerald-500/15',
  'focus-visible:bg-white',
  'hover:border-stone-300',
);

/** Centered card for simple states (loading, error, success) */
function SimpleShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-stone-50">
      <motion.div
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 w-full max-w-[420px] mx-4"
      >
        <div className="rounded-2xl border border-stone-200 p-8 sm:p-10 bg-white shadow-sm">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="flex flex-col items-center text-center"
          >
            {children}
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}

export function InvitationView() {
  const { invitationId } = useParams();
  const { isAuthenticated, registerWithInvitation } = useAuth();
  const { invitation, isLoading, error } = useInvitation(
    invitationId as string
  );
  const [status, setStatus] = useState<'pending' | 'registered'>('pending');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: '',
    },
  });

  if (isAuthenticated()) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (values: RegisterFormData) => {
    if (!invitation) return;
    setIsSubmitting(true);
    try {
      await registerWithInvitation({
        email: invitation.email,
        firstName: values.firstName,
        lastName: values.lastName,
        password: values.password,
        invitationId: invitationId as string,
      });
      setStatus('registered');
    } catch (err) {
      log.error('Registration error', err);
      form.setError('root', {
        message: 'Impossible de créer le compte. Veuillez réessayer.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading
  if (isLoading) {
    return (
      <SimpleShell>
        <motion.div variants={itemVariants}>
          <EikoLogo className="h-14 text-emerald-600" />
        </motion.div>
        <motion.div variants={itemVariants} className="mt-6">
          <Loader2 className="mx-auto h-6 w-6 animate-spin text-emerald-600" />
        </motion.div>
        <motion.p variants={itemVariants} className="mt-3 text-sm text-stone-500">
          Chargement de l'invitation...
        </motion.p>
      </SimpleShell>
    );
  }

  // Error
  if (error || !invitation) {
    return (
      <SimpleShell>
        <motion.div variants={itemVariants}>
          <EikoLogo className="h-14 text-emerald-600" />
        </motion.div>
        <motion.div variants={itemVariants} className="mt-6">
          <XCircle className="mx-auto h-12 w-12 text-red-400" />
        </motion.div>
        <motion.h2 variants={itemVariants} className="mt-4 text-lg font-medium text-stone-800">
          Invitation non trouvée
        </motion.h2>
        <motion.p variants={itemVariants} className="mt-1.5 text-sm text-stone-500">
          Cette invitation peut avoir expiré ou été révoquée.
        </motion.p>
        <motion.div variants={itemVariants} className="mt-6">
          <Link
            to="/login"
            className="text-sm text-stone-400 transition-colors duration-200 hover:text-emerald-600"
          >
            Aller à la connexion
          </Link>
        </motion.div>
      </SimpleShell>
    );
  }

  // Invalid status
  if (invitation.status !== 'PENDING') {
    return (
      <SimpleShell>
        <motion.div variants={itemVariants}>
          <EikoLogo className="h-14 text-emerald-600" />
        </motion.div>
        <motion.div variants={itemVariants} className="mt-6">
          <XCircle className="mx-auto h-12 w-12 text-red-400" />
        </motion.div>
        <motion.h2 variants={itemVariants} className="mt-4 text-lg font-medium text-stone-800">
          Invitation non valide
        </motion.h2>
        <motion.p variants={itemVariants} className="mt-1.5 text-sm text-stone-500">
          Cette invitation a déjà été {invitation.status.toLowerCase()}.
        </motion.p>
        <motion.div variants={itemVariants} className="mt-6">
          <Link
            to="/login"
            className="text-sm text-stone-400 transition-colors duration-200 hover:text-emerald-600"
          >
            Aller à la connexion
          </Link>
        </motion.div>
      </SimpleShell>
    );
  }

  // Success
  if (status === 'registered') {
    return (
      <SimpleShell>
        <motion.div variants={itemVariants}>
          <EikoLogo className="h-14 text-emerald-600" />
        </motion.div>
        <motion.div variants={itemVariants} className="mt-6">
          <CheckCircle className="mx-auto h-14 w-14 text-emerald-500" />
        </motion.div>
        <motion.h2 variants={itemVariants} className="mt-4 text-lg font-medium text-stone-800">
          Compte créé avec succès !
        </motion.h2>
        <motion.p variants={itemVariants} className="mt-1.5 text-sm text-stone-500">
          Vous allez être redirigé vers le tableau de bord...
        </motion.p>
      </SimpleShell>
    );
  }

  // Main form — two-panel layout
  return (
    <div className="relative flex min-h-screen items-center justify-center bg-stone-50 p-4">
      <motion.div
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 w-full max-w-[820px]"
      >
        <div className="rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden grid grid-cols-1 lg:grid-cols-5">

          {/* Left panel — branding + invitation info */}
          <div className="lg:col-span-2 bg-emerald-600 p-8 sm:p-10 flex flex-col justify-between text-white">
            <div>
              <EikoLogo className="h-12 text-white" />
              <h2 className="mt-8 text-xl font-semibold">
                Rejoignez {invitation.organizationName}
              </h2>
              <p className="mt-2 text-sm text-emerald-100">
                Vous avez été invité à rejoindre cette organisation. Créez votre compte pour commencer.
              </p>
            </div>

            <div className="mt-8 flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/15">
                  <Building2 className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-emerald-200">Organisation</p>
                  <p className="text-sm font-medium">{invitation.organizationName}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/15">
                  <Mail className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-wider text-emerald-200">Email</p>
                  <p className="text-sm font-medium truncate">{invitation.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/15">
                  <Shield className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-emerald-200">Rôle</p>
                  <p className="text-sm font-medium">{roleMapping[invitation.role] || invitation.role}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right panel — form */}
          <div className="lg:col-span-3 p-8 sm:p-10">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="flex flex-col"
            >
              <motion.div variants={itemVariants} className="mb-6">
                <h2 className="text-lg font-medium text-stone-800">
                  Créer votre compte
                </h2>
                <p className="mt-1 text-sm text-stone-500">
                  Remplissez les informations ci-dessous
                </p>
              </motion.div>

              <form onSubmit={form.handleSubmit(handleSubmit)}>
                <div className="grid gap-4">
                  {/* Name row */}
                  <div className="grid grid-cols-2 gap-3">
                    <motion.div variants={itemVariants} className="grid gap-2">
                      <Label htmlFor="firstName" className="text-stone-600 text-sm">
                        Prénom
                      </Label>
                      <Input
                        {...form.register('firstName')}
                        id="firstName"
                        placeholder="John"
                        disabled={isSubmitting}
                        className={inputClassName}
                      />
                      {form.formState.errors.firstName && (
                        <p className="text-sm text-red-400">
                          {form.formState.errors.firstName.message}
                        </p>
                      )}
                    </motion.div>

                    <motion.div variants={itemVariants} className="grid gap-2">
                      <Label htmlFor="lastName" className="text-stone-600 text-sm">
                        Nom
                      </Label>
                      <Input
                        {...form.register('lastName')}
                        id="lastName"
                        placeholder="Doe"
                        disabled={isSubmitting}
                        className={inputClassName}
                      />
                      {form.formState.errors.lastName && (
                        <p className="text-sm text-red-400">
                          {form.formState.errors.lastName.message}
                        </p>
                      )}
                    </motion.div>
                  </div>

                  {/* Password */}
                  <motion.div variants={itemVariants} className="grid gap-2">
                    <Label htmlFor="password" className="text-stone-600 text-sm">
                      Mot de passe
                    </Label>
                    <Input
                      {...form.register('password')}
                      id="password"
                      placeholder="Mot de passe"
                      type="password"
                      disabled={isSubmitting}
                      className={inputClassName}
                    />
                    {form.formState.errors.password && (
                      <p className="text-sm text-red-400">
                        {form.formState.errors.password.message}
                      </p>
                    )}
                  </motion.div>

                  {/* Confirm password */}
                  <motion.div variants={itemVariants} className="grid gap-2">
                    <Label htmlFor="confirmPassword" className="text-stone-600 text-sm">
                      Confirmer le mot de passe
                    </Label>
                    <Input
                      {...form.register('confirmPassword')}
                      id="confirmPassword"
                      placeholder="Confirmer le mot de passe"
                      type="password"
                      disabled={isSubmitting}
                      className={inputClassName}
                    />
                    {form.formState.errors.confirmPassword && (
                      <p className="text-sm text-red-400">
                        {form.formState.errors.confirmPassword.message}
                      </p>
                    )}
                  </motion.div>

                  {/* Root error */}
                  {form.formState.errors.root && (
                    <motion.div variants={itemVariants} className="rounded-lg bg-red-50 p-3 text-sm text-red-600 border border-red-200">
                      {form.formState.errors.root.message}
                    </motion.div>
                  )}

                  {/* Submit */}
                  <motion.div variants={itemVariants} className="pt-1">
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className={cn(
                        'relative w-full h-11 rounded-lg text-sm font-semibold',
                        'bg-emerald-600 text-white',
                        'hover:bg-emerald-700',
                        'transition-all duration-300',
                        'disabled:opacity-50',
                      )}
                    >
                      {isSubmitting ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        'Créer le compte'
                      )}
                    </Button>
                  </motion.div>
                </div>
              </form>

              {/* Login link */}
              <motion.div variants={itemVariants} className="mt-5 text-center">
                <Link
                  to="/login"
                  className="text-sm text-stone-400 transition-colors duration-200 hover:text-emerald-600"
                >
                  Vous avez déjà un compte ? Se connecter
                </Link>
              </motion.div>

              {/* Terms */}
              <motion.p
                variants={itemVariants}
                className="mt-4 text-center text-xs text-stone-400 leading-relaxed"
              >
                En cliquant sur continuer, vous acceptez nos{' '}
                <Link
                  to="/terms"
                  className="underline underline-offset-4 transition-colors hover:text-stone-600"
                >
                  Conditions d'utilisation
                </Link>{' '}
                et notre{' '}
                <a
                  href="/privacy"
                  className="underline underline-offset-4 transition-colors hover:text-stone-600"
                >
                  Politique de confidentialité
                </a>
                .
              </motion.p>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
